import { useMemo, useState, useEffect } from "react";
import { NavigationButtons } from "../ui/NavigationButtons";
import { MonthPicker } from "../ui/MonthPicker";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/indexRedux";
import { setDesiredStartDate, setContractEndType, setSpecifiedEndDate } from "../../store/pvCurtailment";
import { SdpPoint } from "../../types/sdpTypes";
import { getFirstOfMonthOnOrAfter } from "../../utils/dateUtils";

type StepNavProps = {
  onNext?: () => void;
  onPrevious?: () => void;
  onSubmit?: () => void;
};

function formatToday() {
  return new Date().toISOString().slice(0, 10);
}

function getFirstDayOfMonth(dateStr: string): string {
  const d = new Date(dateStr);
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

function getFirstDayOfMonthAtLeastOneMonthLater(dateStr: string): string {
  const start = new Date(dateStr);
  const year = start.getFullYear();
  const month = start.getMonth();
  const day = start.getDate();

  let targetMonth = month + 1;
  let targetYear = year;
  if (targetMonth > 11) {
    targetMonth -= 12;
    targetYear++;
  }

  if (day > 1) {
    targetMonth++;
    if (targetMonth > 11) {
      targetMonth -= 12;
      targetYear++;
    }
  }

  return `${targetYear}-${String(targetMonth + 1).padStart(2, "0")}-01`;
}

function ContractPar({ onNext, onPrevious }: StepNavProps) {
  const dispatch = useDispatch();

  const todayStr = formatToday();

  const {
    desiredStartDate: reduxDesired,
    // actualStartDate: reduxActual,
    contractEndType: reduxEndType,
    specifiedEndDate: reduxSpecifiedEnd,
    selectedInjection,
  } = useSelector((state: RootState) => state.pvCurtailment);

  // For UX we keep attempted/touched locally
  const [attemptedNext, setAttemptedNext] = useState(false);
  const [touched, setTouched] = useState({
    desired: false,
    specifiedEnd: false,
  });

  const injectionContractStartDate = useMemo(() => {
    if (!selectedInjection) return null;

    const injection = selectedInjection as SdpPoint;
    const contractDate = injection.startDate || injection.signatureDate;
    if (!contractDate) return null;

    return contractDate.split("T")[0].split(" ")[0];
  }, [selectedInjection]);

  const injectionContractEndDate = useMemo(() => {
    if (!selectedInjection) return null;

    const injection = selectedInjection as SdpPoint;
    if (!injection.endDate) return null;

    return injection.endDate.split("T")[0].split(" ")[0];
  }, [selectedInjection]);

  const hasBessOrEboiler = useMemo(() => {
    const inj = selectedInjection as SdpPoint | null;
    return inj?.bess?.has_active_contract === 1 || inj?.eboiler?.has_active_contract === 1;
  }, [selectedInjection]);

  const minAllowedStartDate = useMemo(() => {
    const base =
      injectionContractStartDate && injectionContractStartDate > todayStr
        ? injectionContractStartDate
        : todayStr;
    return hasBessOrEboiler ? getFirstOfMonthOnOrAfter(base) : base;
  }, [injectionContractStartDate, todayStr, hasBessOrEboiler]);

  useEffect(() => {
    const needsReset =
      !reduxDesired ||
      reduxDesired.trim() === "" ||
      (hasBessOrEboiler && new Date(reduxDesired).getDate() !== 1);

    if (needsReset) {
      dispatch(setDesiredStartDate(minAllowedStartDate));
    }

    if (!reduxEndType) {
      dispatch(setContractEndType("auto"));
    }
  }, [dispatch, reduxDesired, reduxEndType, minAllowedStartDate, hasBessOrEboiler]);

  const desiredStartDate = reduxDesired || minAllowedStartDate;

  const contractEndType = (reduxEndType as "auto" | "date") || "auto";
  const specifiedEndDate = reduxSpecifiedEnd || "";

  const handleNext = onNext ?? (() => {});
  const handlePrevious = onPrevious ?? (() => {});

  const isDesiredMissing = !desiredStartDate || desiredStartDate.trim() === "";

  const isDesiredInvalid = useMemo(() => {
    if (!desiredStartDate) return false;
    return desiredStartDate < minAllowedStartDate;
  }, [desiredStartDate, minAllowedStartDate]);

  const minAllowedEndDate = useMemo(() => {
    if (!desiredStartDate) return "";
    return getFirstDayOfMonthAtLeastOneMonthLater(desiredStartDate);
  }, [desiredStartDate]);

  const maxAllowedEndDate = useMemo(() => {
    if (!injectionContractEndDate) return null;
    return getFirstDayOfMonth(injectionContractEndDate);
  }, [injectionContractEndDate]);

  const isSpecifiedMissing = contractEndType === "date" && (!specifiedEndDate || specifiedEndDate.trim() === "");
  const isEndTooSoon = contractEndType === "date" && specifiedEndDate ? specifiedEndDate < minAllowedEndDate : false;
  const isEndTooLate =
    contractEndType === "date" && specifiedEndDate && maxAllowedEndDate ? specifiedEndDate > maxAllowedEndDate : false;

  const blockNext =
    isDesiredMissing ||
    isDesiredInvalid ||
    (contractEndType === "date" && (isSpecifiedMissing || isEndTooSoon || isEndTooLate));

  function onChangeDesired(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    dispatch(setDesiredStartDate(v));
    setTouched((s) => ({ ...s, desired: true }));
  }

  function onChangeContractEndType(e: React.ChangeEvent<HTMLSelectElement>) {
    const val = e.target.value as "auto" | "date";
    dispatch(setContractEndType(val));
    setTouched((s) => ({ ...s, specifiedEnd: true }));
  }

  function onChangeSpecifiedEnd(val: string) {
    dispatch(setSpecifiedEndDate(val));
    setTouched((s) => ({ ...s, specifiedEnd: true }));
  }

  function tryNext() {
    setAttemptedNext(true);
    setTouched({ desired: true, specifiedEnd: true });
    if (!blockNext) {
      handleNext();
    }
  }

  const getDesiredDateErrorMessage = () => {
    if (injectionContractStartDate) {
      return `Desired start date must be equal to or later than the injection contract start date (${injectionContractStartDate}) and cannot be in the past.`;
    }
    return "Desired start date cannot be in the past.";
  };

  return (
    <div>
      <div className="mb-12">
        <h1 className="text-primary-600 dark:text-white text-3xl font-semibold mb-6">Contract parameters</h1>
        <p className="text-primary-500 dark:text-gray-300 font-normal text-base text-md">
          Configure the contract start and end dates. Desired start date is required.
        </p>
      </div>

      <div className="bg-white dark:bg-primary-900 p-12 rounded-lg w-full border-[2px] border-gray-100 dark:border-primary-800">
        <form className="flex flex-col gap-6 text-sm" onSubmit={(e) => e.preventDefault()}>
          <div className="flex gap-6">
            <div className="flex flex-1 flex-col gap-3">
              <label className="font-semibold text-primary-600 dark:text-white" htmlFor="desired-start-date">
                Desired start date <span className="text-red-600">*</span>
              </label>
              {hasBessOrEboiler ? (
                <MonthPicker
                  value={desiredStartDate}
                  onChange={(val) => {
                    dispatch(setDesiredStartDate(val));
                    setTouched((s) => ({ ...s, desired: true }));
                  }}
                  minDate={minAllowedStartDate}
                  maxDate={maxAllowedEndDate ?? undefined}
                  placeholder="Select start month"
                  hasError={(attemptedNext || touched.desired) && (isDesiredMissing || isDesiredInvalid)}
                />
              ) : (
                <input
                  id="desired-start-date"
                  type="date"
                  className={`border p-3 text-primary-500 dark:text-white dark:bg-primary-800 dark:border-primary-700 ${
                    (attemptedNext || touched.desired) && (isDesiredMissing || isDesiredInvalid) ? "border-red-400" : ""
                  }`}
                  value={desiredStartDate}
                  onChange={onChangeDesired}
                  onBlur={() => setTouched((s) => ({ ...s, desired: true }))}
                  min={minAllowedStartDate}
                />
              )}
              {(attemptedNext || touched.desired) && isDesiredMissing && (
                <p className="text-xs text-red-600">Desired start date is required.</p>
              )}
              {(attemptedNext || touched.desired) && !isDesiredMissing && isDesiredInvalid && (
                <p className="text-xs text-red-600">{getDesiredDateErrorMessage()}</p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Once the contract is signed, Yuso will initiate the technical onboarding of the asset, taking into
                account the desired start date. Only after this technical onboarding is finished, the steering of
                the asset can start. For new installations, the AREI control has to be executed as well.
              </p>
              {hasBessOrEboiler && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  The start date must fall on the 1st of a month.
                </p>
              )}
              {injectionContractStartDate && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Minimum allowed date: {minAllowedStartDate} (injection contract start date:{" "}
                  {injectionContractStartDate})
                </p>
              )}
              {!hasBessOrEboiler && Boolean(selectedInjection?.isFuture) && injectionContractStartDate && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  The injection contract starts on {injectionContractStartDate}. PV curtailment
                  cannot start before this date.
                </p>
              )}
            </div>

            {/* <div className="flex flex-1 flex-col gap-3">
              <label className="font-semibold text-primary-600" htmlFor="actual-start-date">
                Actual start date of service
              </label>
              <input
                id="actual-start-date"
                type="date"
                className="border p-3 text-primary-500 bg-gray-50 cursor-not-allowed"
                value={actualStartDate ?? ""}
                onChange={() => {}}
                disabled
              />
              <p className="text-xs text-gray-500">Only sales team may edit the actual start date.</p>
            </div> */}
          </div>

          <div className="flex flex-col gap-3">
            <label className="font-semibold text-primary-600 dark:text-white" htmlFor="contract-end-type">
              Contract end
            </label>
            <select
              id="contract-end-type"
              className="border p-3 text-primary-500 dark:text-white dark:bg-primary-800 dark:border-primary-700 w-full"
              value={contractEndType}
              onChange={onChangeContractEndType}
            >
              <option value="auto">Automatic renewal</option>
              <option value="date">Specify end date</option>
            </select>

            {contractEndType === "date" && (
              <div className="mt-2 flex flex-col gap-2">
                <label className="font-semibold text-primary-600 dark:text-white" htmlFor="specified-end-date">
                  End date <span className="text-red-600">*</span>
                </label>
                <MonthPicker
                  value={specifiedEndDate}
                  onChange={onChangeSpecifiedEnd}
                  minDate={minAllowedEndDate || todayStr}
                  maxDate={maxAllowedEndDate}
                  placeholder="Select end month"
                  hasError={
                    (attemptedNext || touched.specifiedEnd) && (isSpecifiedMissing || isEndTooSoon || isEndTooLate)
                  }
                />
                {(attemptedNext || touched.specifiedEnd) && isSpecifiedMissing && (
                  <p className="text-xs text-red-600">Please specify an end date.</p>
                )}
                {(attemptedNext || touched.specifiedEnd) && !isSpecifiedMissing && isEndTooSoon && (
                  <p className="text-xs text-red-600">
                    End date must be at least 1 month after the desired start date (minimum: {minAllowedEndDate}).
                  </p>
                )}
                {(attemptedNext || touched.specifiedEnd) && !isSpecifiedMissing && !isEndTooSoon && isEndTooLate && (
                  <p className="text-xs text-red-600">
                    End date cannot be later than the injection contract end date (maximum: {maxAllowedEndDate}).
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  The end date must fall on the 1st of a month, at least one full month after the start date.
                  {injectionContractEndDate && (
                    <>
                      {" "}
                      If the injection contract has an end date ({injectionContractEndDate}), this date cannot exceed
                      it.
                    </>
                  )}
                </p>
              </div>
            )}
          </div>
        </form>

        <NavigationButtons currentStep={3} totalSteps={4} onPrevious={handlePrevious} onNext={tryNext} />
      </div>
    </div>
  );
}

export default ContractPar;
